/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { SetStateAction, useCallback, useMemo, useState } from 'react'
import RequirePerm from '../../../auth/components/RequirePerm'
import { FormattedMessage } from 'react-intl'
import SearchForm from '../../../../client/js/components/search/SearchForm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons/faSyncAlt'
import { faFilter } from '@fortawesome/free-solid-svg-icons/faFilter'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import Filters from '../../../../client/js/components/Filters'
import CustomerTypesFilter from '../filters/CustomerTypesFilter'
import WithMobileFilter from '../filters/WithMobileFilter'
import WithPhoneFilter from '../filters/WithPhoneFilter'
import WithEmailFilter from '../filters/WithEmailFilter'
import WithBirthDateFilter from '../filters/WithBirthDateFilter'
import WithProfessionFilter from '../filters/WithProfessionFilter'
import WithRibFilter from '../filters/WithRibFilter'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import CustomerList from './CustomerList'
import DeleteSelectionModal from '../../../../client/js/components/modals/DeleteSelectionModal'
import ListCard from '../../../../client/js/components/ListCard'
import { QueryParams } from '../../../../client/js/hooks/useQueryParams'
import usePaginationQuery from '../../../../client/js/components/pagination/usePaginationQuery'
import { useQuery } from '@tanstack/react-query'
import {
  deleteCustomer,
  downloadCustomerVCards,
  findManyCustomers
} from '../../lib/rest/customerAPIClient'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import useSelection from '../../../../client/js/components/selection/useSelection'
import { getCustomerCreateLink } from '../../lib/links/customerLinks'
import { Button } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import Toolbar from '../../../../client/js/components/Toolbar'
import SelectionAlert from '../../../../client/js/components/selection/SelectionAlert'
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'

export type CustomerListQuery = {
  limit?: string,
  page?: string,
  orderBy?: string,
  isLessor?: string,
  isProspect?: string,
  isServiceProvider?: string,
  isTenant?: string,
  search?: string,
  withBirthDate?: string,
  withEmail?: string,
  withMobile?: string,
  withPhone?: string,
  withProfession?: string,
  withRib?: string,
}

export type CustomerListCardProps = {
  onQueryChange (query: SetStateAction<CustomerListQuery>): void,
  query: CustomerListQuery,
}

const initialQuery: CustomerListQuery = {
  limit: '10',
  page: '1',
  orderBy: [
    'modifiedAt.desc',
    'lastName',
    'firstName'
  ].join(',')
}

function CustomerListCard (props: CustomerListCardProps) {
  const { query, onQueryChange } = props
  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { page, limit } = pagination

  const queryParams = useMemo(() => {
    return {
      ...initialQuery,
      ...query,
      page,
      limit
    }
  }, [limit, page, query])

  const listQuery = useQuery({
    enabled: true,
    queryKey: ['customers', queryParams],
    queryFn: () => findManyCustomers(queryParams).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })

  const items = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.customers : null) || []
  ), [listQuery.data])

  const downloadQuery = useQuery({
    enabled: false,
    queryKey: ['customerVCards', queryParams],
    queryFn: () => downloadCustomerVCards(queryParams)
  })

  const setFilter = useCallback((name: string, value?: unknown) => {
    onQueryChange((s: QueryParams) => ({ ...s, page: '1', [name]: value }))
  }, [onQueryChange])

  const setSearch = useCallback((search?: string) => {
    setFilter('search', search)
  }, [setFilter])

  // Items selection.
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const selection = useSelection<number>({
    multiple: true,
    items: selectedItems,
    onChange: setSelectedItems,
    source: items.map((el) => el.id)
  })

  // Items deletion.
  const handleDelete = useCallback((id: number) => deleteCustomer(id), [])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const toggleDeleteModal = useCallback(() => {
    setIsDeleteModalOpen((s) => !s)
  }, [])

  // Items filtering.
  const [isFiltersVisible, setIsFiltersVisible] = useState(true)
  const toggleFilters = useCallback(() => {
    setIsFiltersVisible((s) => !s)
  }, [])

  return (
    <ListCard
      filters={<Filters in={isFiltersVisible}>
        <CustomerTypesFilter
          onChange={setFilter}
          value={queryParams}
        />
        <WithMobileFilter
          onChange={setFilter}
          value={queryParams.withMobile}
        />
        <WithPhoneFilter
          onChange={setFilter}
          value={queryParams.withPhone}
        />
        <WithEmailFilter
          onChange={setFilter}
          value={queryParams.withEmail}
        />
        <WithBirthDateFilter
          onChange={setFilter}
          value={queryParams.withBirthDate}
        />
        <WithProfessionFilter
          onChange={setFilter}
          value={queryParams.withProfession}
        />
        <WithRibFilter
          onChange={setFilter}
          value={queryParams.withRib}
        />
      </Filters>}
      pagination={pagination}
      selection={selection}
      selectionActions={<SelectionAlert>
        <RequirePerm
          permissions={['write:customer']}
          render={() => (
            <Button onClick={toggleDeleteModal}>
              <FontAwesomeIcon
                fixedWidth
                icon={faTrash}
              />
            </Button>
          )}
        />
      </SelectionAlert>}
      toolbar={<Toolbar>
        <RequirePerm
          permissions={['write:customer']}
          render={() => (
            <Button
              variant="filled"
              component={AnchorLink}
              to={getCustomerCreateLink()}
            >
              <FormattedMessage
                id="create_customer"
                defaultMessage="Créer un contact"
              />
            </Button>
          )}
        />
        <SearchForm
          initialValue={queryParams.search}
          onSubmit={setSearch}
          submitting={listQuery.isFetching}
        />
        <Button
          loading={listQuery.isFetching}
          onClick={() => {
            listQuery.refetch()
          }}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faSyncAlt}
          />
        </Button>
        <Button
          // todo active={isFiltersVisible}
          onClick={toggleFilters}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faFilter}
          />
        </Button>
        <RequirePerm
          permissions={['export:customer']}
          render={() => (
            <Button
              loading={downloadQuery.isFetching}
              onClick={() => {
                downloadQuery.refetch()
              }}
            >
              <FontAwesomeIcon fixedWidth icon={faDownload} />
            </Button>
          )}
        />
      </Toolbar>}
    >
      <LoadingHelper
        query={listQuery}
        render={() => (
          <CustomerList items={items} />
        )}
      />

      {isDeleteModalOpen
        ? <DeleteSelectionModal
          opened={isDeleteModalOpen}
          items={selection.items}
          onConfirm={handleDelete}
          onSuccess={() => {
            selection.clear()
            listQuery.refetch()
          }}
          onClose={toggleDeleteModal}
        />
        : null}
    </ListCard>
  )
}

export default CustomerListCard
